import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useCanonical } from '../../hooks/useCanonical.js';
import { helpSections } from './helpData.js';

export function HelpView() {
  useCanonical('https://ridehub.shiget.net/help');
  const navigate = useNavigate();

  useEffect(() => {
    const originalTitle = document.title;
    document.title = '使い方ガイド | RideHub';

    const metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const originalDesc = metaDesc?.content ?? '';
    if (metaDesc) {
      metaDesc.content =
        'RideHubの使い方ガイド。マップでのスポット検索・チェックイン・スポット登録・ダイレクトメッセージなど、基本的な使い方をステップごとに説明します。';
    }

    // プリレンダリング済みHTMLに既にJSON-LDが含まれている場合は重複を避けてスキップ
    const existing = document.head.querySelector('script[type="application/ld+json"]');
    if (existing) {
      return () => {
        document.title = originalTitle;
        if (metaDesc) metaDesc.content = originalDesc;
      };
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'RideHub の使い方',
      description:
        'サイクリスト向けスポット共有アプリ RideHub の使い方をステップごとに解説します。',
      step: helpSections.map((section) => ({
        '@type': 'HowToSection',
        name: section.title,
        itemListElement: section.points.map((point) => ({
          '@type': 'HowToStep',
          text: point,
        })),
      })),
    });
    document.head.appendChild(script);

    return () => {
      document.title = originalTitle;
      if (metaDesc) metaDesc.content = originalDesc;
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="max-w-lg mx-auto pb-8">
      {/* ヘッダー */}
      <div className="flex items-center gap-3 p-4 mb-2">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-gray-700"
          aria-label="戻る"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 className="text-xl font-bold">使い方ガイド</h1>
      </div>

      {/* セクション一覧 */}
      <div className="space-y-10 px-4">
        {helpSections.map((section, index) => (
          <div key={index}>
            <h2 className="text-base font-bold text-gray-800 mb-3">
              {index + 1}. {section.title}
            </h2>

            {/* スクリーンショット */}
            <div className="flex justify-center mb-3">
              <div className="rounded-xl overflow-hidden border border-gray-100 shadow-sm w-48">
                <img src={section.image} alt={section.title} className="w-full" />
              </div>
            </div>

            {/* 説明文 */}
            <p className="text-sm text-gray-600 mb-2">{section.description}</p>

            {/* ポイント箇条書き */}
            <ul className="space-y-1.5">
              {section.points.map((point, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* フッター */}
      <div className="mt-10 px-4 text-center text-xs text-gray-400">
        <p>ご不明な点はフィードバックからお知らせください</p>
      </div>
    </div>
  );
}
